/**
 * Centralized realtime connection manager for Supabase
 * Prevents connection leaks and manages subscription lifecycle
 */

import { useEffect } from 'react';
import { supabase } from './supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface ChannelSubscription {
  channel: RealtimeChannel;
  subscriptionCount: number;
  lastActivity: number;
  errorCount: number;
  lastError?: Error;
}

class RealtimeManager {
  private channels: Map<string, ChannelSubscription> = new Map();
  private cleanupInterval: NodeJS.Timeout | null = null;
  private readonly CHANNEL_TIMEOUT = 30000; // 30 seconds
  private readonly CLEANUP_INTERVAL = 10000; // 10 seconds
  private readonly MAX_ERROR_COUNT = 5; // Max errors before force cleanup

  constructor() {
    // Start cleanup interval
    this.cleanupInterval = setInterval(() => {
      this.cleanupInactiveChannels();
    }, this.CLEANUP_INTERVAL);

    // Clean up on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.cleanup();
      });
    }
  }

  /**
   * Subscribe to a channel with automatic cleanup
   */
  subscribe(
    channelName: string,
    callback: (payload: any) => void,
    options: {
      event?: string;
      schema?: string;
      table?: string;
      filter?: string;
      onError?: (error: Error) => void;
      onStatusChange?: (status: string) => void;
    } = {}
  ): () => void {
    const {
      event = '*',
      schema = 'public',
      table,
      filter,
      onError,
      onStatusChange
    } = options;

    let subscription = this.channels.get(channelName);

    if (!subscription) {
      // Create new channel
      const channel = supabase.channel(channelName);
      
      // Create error-wrapped callback
      const wrappedCallback = (payload: any) => {
        try {
          callback(payload);
          // Reset error count on successful callback
          const sub = this.channels.get(channelName);
          if (sub) {
            sub.errorCount = 0;
            sub.lastActivity = Date.now();
          }
        } catch (error) {
          console.error(`Realtime callback error for channel ${channelName}:`, error);
          
          // Track errors
          const sub = this.channels.get(channelName);
          if (sub) {
            sub.errorCount++;
            sub.lastError = error as Error;
            
            // Force cleanup if too many errors
            if (sub.errorCount >= this.MAX_ERROR_COUNT) {
              console.warn(`Force cleaning up channel ${channelName} due to excessive errors`);
              this.forceCleanup(channelName);
            }
          }
          
          // Call error handler if provided
          if (onError) {
            onError(error as Error);
          }
        }
      };

      // Configure the channel based on options
      if (table) {
        channel.on('postgres_changes', {
          event: event as any,
          schema,
          table,
          ...(filter ? { filter } : {})
        }, wrappedCallback);
      } else {
        // For broadcast or presence channels
        channel.on(event, wrappedCallback);
      }

      // Subscribe to the channel with enhanced error handling
      channel.subscribe((status, error) => {
        if (import.meta.env.DEV) {
          console.log(`Realtime channel ${channelName} status:`, status);
          if (error) {
            console.error(`Realtime channel ${channelName} error:`, error);
          }
        }

        // Call status change handler if provided
        if (onStatusChange) {
          onStatusChange(status);
        }

        // Handle subscription errors
        if (status === 'CHANNEL_ERROR' || error) {
          const sub = this.channels.get(channelName);
          if (sub) {
            sub.errorCount++;
            sub.lastError = error as Error;
            
            if (onError && error) {
              onError(error as Error);
            }
            
            // Force cleanup if too many errors
            if (sub.errorCount >= this.MAX_ERROR_COUNT) {
              console.warn(`Force cleaning up channel ${channelName} due to subscription errors`);
              this.forceCleanup(channelName);
            }
          }
        }
      });

      subscription = {
        channel,
        subscriptionCount: 1,
        lastActivity: Date.now(),
        errorCount: 0
      };

      this.channels.set(channelName, subscription);
    } else {
      // Increment subscription count for existing channel
      subscription.subscriptionCount++;
      subscription.lastActivity = Date.now();
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(channelName);
    };
  }

  /**
   * Unsubscribe from a channel
   */
  private unsubscribe(channelName: string): void {
    const subscription = this.channels.get(channelName);
    
    if (!subscription) {
      return;
    }

    subscription.subscriptionCount--;

    if (subscription.subscriptionCount <= 0) {
      // Remove the channel
      supabase.removeChannel(subscription.channel);
      this.channels.delete(channelName);
      
      if (import.meta.env.DEV) {
        console.log(`Realtime channel ${channelName} removed`);
      }
    }
  }

  /**
   * Clean up inactive channels and channels with excessive errors
   */
  private cleanupInactiveChannels(): void {
    const now = Date.now();
    const channelsToRemove: string[] = [];

    this.channels.forEach((subscription, channelName) => {
      const isInactive = now - subscription.lastActivity > this.CHANNEL_TIMEOUT;
      const hasExcessiveErrors = subscription.errorCount >= this.MAX_ERROR_COUNT;
      
      if (isInactive || hasExcessiveErrors) {
        channelsToRemove.push(channelName);
      }
    });

    channelsToRemove.forEach(channelName => {
      const subscription = this.channels.get(channelName);
      if (subscription) {
        try {
          supabase.removeChannel(subscription.channel);
        } catch (error) {
          console.warn(`Error removing channel ${channelName}:`, error);
        }
        
        this.channels.delete(channelName);
        
        if (import.meta.env.DEV) {
          const reason = subscription.errorCount >= this.MAX_ERROR_COUNT ? 'excessive errors' : 'inactivity';
          console.log(`Realtime channel ${channelName} cleaned up due to ${reason}`);
        }
      }
    });
  }

  /**
   * Update activity timestamp for a channel
   */
  updateActivity(channelName: string): void {
    const subscription = this.channels.get(channelName);
    if (subscription) {
      subscription.lastActivity = Date.now();
    }
  }

  /**
   * Get active channel count
   */
  getActiveChannelCount(): number {
    return this.channels.size;
  }

  /**
   * Get channel info for debugging
   */
  getChannelInfo(): Array<{ 
    name: string; 
    subscriptionCount: number; 
    lastActivity: Date;
    errorCount: number;
    lastError?: string;
  }> {
    return Array.from(this.channels.entries()).map(([name, subscription]) => ({
      name,
      subscriptionCount: subscription.subscriptionCount,
      lastActivity: new Date(subscription.lastActivity),
      errorCount: subscription.errorCount,
      lastError: subscription.lastError?.message
    }));
  }

  /**
   * Reset error count for a specific channel
   */
  resetChannelErrors(channelName: string): void {
    const subscription = this.channels.get(channelName);
    if (subscription) {
      subscription.errorCount = 0;
      subscription.lastError = undefined;
      
      if (import.meta.env.DEV) {
        console.log(`Reset error count for channel: ${channelName}`);
      }
    }
  }

  /**
   * Get health status of realtime manager
   */
  getHealthStatus(): {
    totalChannels: number;
    healthyChannels: number;
    errorChannels: number;
    overallHealth: 'healthy' | 'degraded' | 'unhealthy';
  } {
    let healthyChannels = 0;
    let errorChannels = 0;
    
    this.channels.forEach(subscription => {
      if (subscription.errorCount === 0) {
        healthyChannels++;
      } else {
        errorChannels++;
      }
    });

    const totalChannels = this.channels.size;
    const errorRatio = totalChannels > 0 ? errorChannels / totalChannels : 0;
    
    let overallHealth: 'healthy' | 'degraded' | 'unhealthy';
    if (errorRatio === 0) {
      overallHealth = 'healthy';
    } else if (errorRatio < 0.5) {
      overallHealth = 'degraded';
    } else {
      overallHealth = 'unhealthy';
    }

    return {
      totalChannels,
      healthyChannels,
      errorChannels,
      overallHealth
    };
  }

  /**
   * Clean up all channels
   */
  cleanup(): void {
    this.channels.forEach((subscription, channelName) => {
      supabase.removeChannel(subscription.channel);
    });
    
    this.channels.clear();

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    if (import.meta.env.DEV) {
      console.log('Realtime manager cleaned up all channels');
    }
  }

  /**
   * Force cleanup of a specific channel
   */
  forceCleanup(channelName: string): void {
    const subscription = this.channels.get(channelName);
    if (subscription) {
      supabase.removeChannel(subscription.channel);
      this.channels.delete(channelName);
      
      if (import.meta.env.DEV) {
        console.log(`Force cleaned up realtime channel: ${channelName}`);
      }
    }
  }
}

// Export singleton instance
export const realtimeManager = new RealtimeManager();

// Development utilities
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__realtimeManager = {
    getChannelCount: () => realtimeManager.getActiveChannelCount(),
    getChannelInfo: () => realtimeManager.getChannelInfo(),
    getHealthStatus: () => realtimeManager.getHealthStatus(),
    resetErrors: (channelName: string) => realtimeManager.resetChannelErrors(channelName),
    cleanup: () => realtimeManager.cleanup(),
    forceCleanup: (channelName: string) => realtimeManager.forceCleanup(channelName),
    // Quick debug helpers
    listChannels: () => {
      const channels = realtimeManager.getChannelInfo();
      console.table(channels);
      return channels;
    },
    checkHealth: () => {
      const health = realtimeManager.getHealthStatus();
      console.log('Realtime Manager Health:', health);
      return health;
    }
  };
}

/**
 * Hook for components to easily subscribe to realtime updates
 */
export function useRealtimeSubscription(
  channelName: string,
  callback: (payload: any) => void,
  options: {
    event?: string;
    schema?: string;
    table?: string;
    filter?: string;
    enabled?: boolean;
  } = {}
) {
  const { enabled = true, ...subscriptionOptions } = options;

  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = realtimeManager.subscribe(channelName, callback, subscriptionOptions);

    return unsubscribe;
  }, [channelName, callback, enabled, JSON.stringify(subscriptionOptions)]);

  // Update activity on each render to keep channel alive
  useEffect(() => {
    if (enabled) {
      realtimeManager.updateActivity(channelName);
    }
  });
}

/**
 * Utility function to create unique channel names
 */
export function createChannelName(prefix: string, id: string): string {
  return `${prefix}_${id}_${Date.now()}`;
}